import axios from 'axios'
import { errorHandler, getUrl } from '@/api/utils/http-helpers.js'

/**
 * MUTATIONS TYPES
 */
const PALLET_PATTERN_LOAD = 'PALLET_PATTERN_LOAD'
const PALLET_PATTERN_SELECT = 'PALLET_PATTERN_SELECT'
const PALLET_PATTERN_ADD = 'PALLET_PATTERN_ADD'
const PALLET_PATTERN_UPDATE = 'PALLET_PATTERN_UPDATE'
const PALLET_PATTERN_REMOVE = 'PALLET_PATTERN_REMOVE'
const START = 'START'
const END = 'END'
const ERROR = 'ERROR'
const INCREMENT = 'INCREMENT'
const DECREMENT = 'DECREMENT'
const INVALID = 'INVALID'
const VALID = 'VALID'
const VALIDATE_STEP = 'VALIDATE_STEP'
const VALIDATE_ALL = 'VALIDATE_ALL'
const INVALIDATE_ALL = 'INVALIDATE_ALL'
const SET_STEP = 'SET_STEP'
const CLOSE_ERROR = 'CLOSE_ERROR'
const SET_NEW = 'SET_NEW'
const SET_SENDING = 'SET_SENDING'
const REMOVE_SENDING = 'REMOVE_SENDING'
const SET_VALIDATION_PROGRESS = 'SET_VALIDATION_PROGRESS'
const CLEAR_VALIDATION_PROGRESS = 'CLEAR_VALIDATION_PROGRESS'

/**
 * State
 */
const initialState = {
  entities: [],
  selectedEntity: {},
  steps: [
    { id: 1, state: false, title: 'steps.basic', name: 'menu.basic' },
    { id: 2, state: false, title: 'steps.box', name: 'menu.box' },
    { id: 3, state: false, title: 'steps.robot', name: 'menu.robot' },
    { id: 4, state: false, title: 'steps.pallet', name: 'menu.pallet' },
    { id: 5, state: false, title: 'steps.pattern', name: 'menu.pattern' },
    { id: 6, state: false, title: 'steps.layer', name: 'menu.layer' },
    { id: 7, state: false, title: 'steps.overview', name: 'menu.overview' }
  ],
  currentStep: 1,
  error: false,
  isLoading: false,
  isLoaded: false,
  isNew: false,
  isSending: '',
  validationProgress: {}
}

const state = { ...initialState }

/**
 * GETTERS
 */
const getters = {
  selectedEntity (state) {
    return state.selectedEntity
  },

  palletPatternById: state => id => {
    return state.entities.find(entity => entity.guid === id)
  },

  palletPatterns (state) {
    return state.entities
  },

  palletPatternsCount (state) {
    return state.entities.length
  },

  isLoading (state) {
    return state.isLoading
  },

  isLoaded (state) {
    return state.isLoaded
  },

  error (state) {
    return state.error
  },

  stepById: () => id => {
    const step = state.steps.find(step => step.id === id)
    return step.state
  },

  steps (state) {
    return state.steps
  },

  currentStep (state) {
    return state.currentStep
  },

  checkName: () => name => {
    return state.entities.some(
      step =>
        (state.isNew && step.name.toLowerCase() === name.toLowerCase()) ||
        (!state.isNew && step.name === name && step.guid !== state.selectedEntity.guid)
    )
  },

  isNew (state) {
    return state.isNew
  },

  validationProgressByGuid: state => guid => {
    return state.validationProgress[guid]
  }

}

/**
 * ACTIONS
 */
const actions = {
  async getSelectedEntity ({ commit }, payload) {
    const url = getUrl(`/pallet-patterns/${payload}`)
    commit('START')
    await axios
      .get(url)
      .then(resp => {
        if (resp.data) {
          // console.log('[DEBUG] PALLET_PATTERN_SELECT', resp.data)
          commit('PALLET_PATTERN_SELECT', resp.data)
          commit('END')
        }
      })
      .catch(e => {
        errorHandler(e)
        commit('ERROR')
        commit('END')
      })
  },

  async loadPalletPatterns ({ dispatch, commit }) {
    const url = getUrl(`/pallet-patterns`)
    commit('START')
    await axios
      .get(url)
      .then(resp => {
        if (resp.data) {
          commit('PALLET_PATTERN_LOAD', resp.data)
          commit('END')
        }
      })
      .catch(e => {
        errorHandler(e)
        commit('ERROR')
        commit('END')
      })
  },

  async addPalletPattern ({ dispatch, commit }, payload) {
    console.log('[DEBUG] $store.addPalletPattern', payload)
    const url = getUrl(`/pallet-patterns`)
    commit('START')
    await axios
      .post(url, payload)
      .then(resp => {
        if (resp.data) {
          commit('PALLET_PATTERN_ADD', resp.data)
          // dispatch('patternimages/addCollection', resp.data.guid, { root: true })
          commit('VALID')
          commit('INCREMENT')
          commit('END')
        }
      })
      .catch(e => {
        errorHandler(e)
        commit('ERROR')
        commit('INVALID')
        commit('END')
      })
  },

  async updatePalletPattern ({ commit }, payload) {
    console.log('[DEBUG] $store.updatePalletPattern', payload)
    const url = getUrl(`/pallet-patterns/${payload.guid}`)
    const palletPattern = payload.palletPattern
    commit('START')
    await axios
      .put(url, palletPattern)
      .then(resp => {
        if (resp.data) {
          commit('PALLET_PATTERN_UPDATE', resp.data)
          commit('VALID')
          if (state.currentStep !== 7) {
            commit('INCREMENT')
          }
          commit('END')
        }
      })
      .catch(e => {
        errorHandler(e)
        console.log('[ERROR] e: ', e)
        commit('ERROR')
        commit('INVALID')
        commit('END')
      })
  },

  async removePalletPattern ({ dispatch, commit }, payload) {
    const url = getUrl(`/pallet-patterns/${payload.guid}`)
    commit('START')
    await axios
      .delete(url)
      .then(resp => {
        if (resp) {
          commit('PALLET_PATTERN_REMOVE', payload)
          // dispatch('patternimages/removeCollection', payload, { root: true })
          commit('END')
        }
      })
      .catch(e => {
        errorHandler(e)
        commit('ERROR')
        commit('END')
      })
  },

  validateById ({ commit }, payload) {
    commit('VALIDATE_STEP', payload)
  },

  validateAll ({ commit }) {
    commit('VALIDATE_ALL')
  },

  invalidateAll ({ commit }) {
    commit('INVALIDATE_ALL')
  },

  setStep ({ commit }, payload) {
    commit('SET_STEP', payload)
  },

  decrement ({ commit }) {
    commit('DECREMENT')
  },

  closeError ({ commit }) {
    commit('CLOSE_ERROR')
  },

  setNew ({ commit }, payload) {
    commit('SET_NEW', payload)
  },

  setSending ({ commit }, payload) {
    commit('SET_SENDING', payload)
  },

  removeSending ({ commit }, payload) {
    commit('REMOVE_SENDING', payload)
  },

  setValidationProgress ({ commit }, payload) {
    commit('SET_VALIDATION_PROGRESS', payload)
  },

  clearValidationProgress ({ commit }, payload) {
    commit('CLEAR_VALIDATION_PROGRESS', payload)
  }
}

/**
 * MUTATIONS
 */
const mutations = {
  [START] (state) {
    state.isLoaded = false
    state.isLoading = true
  },

  [END] (state) {
    state.isLoading = false
    state.isLoaded = true
  },

  [PALLET_PATTERN_SELECT] (state, payload) {
    state.selectedEntity = Object.assign({}, payload)
  },

  [PALLET_PATTERN_LOAD] (state, payload) {
    state.entities = Object.assign([], state.entities, payload)
  },

  [PALLET_PATTERN_ADD] (state, payload) {
    state.entities = Object.assign([], state.entities, payload)
    state.selectedEntity = Object.assign({}, payload)
    state.isNew = false
  },

  [PALLET_PATTERN_UPDATE] (state, payload) {
    const entity = state.entities.find(entity => {
      return entity.guid === payload.guid
    })
    const index = state.entities.indexOf(entity)
    if (index > -1) {
      state.entities[index] = payload
    }
    state.selectedEntity = Object.assign({}, payload)
    state.isNew = false
  },

  [PALLET_PATTERN_REMOVE] (state, payload) {
    const entity = payload
    const index = state.entities.indexOf(entity)
    if (index > -1) {
      state.entities.splice(index, 1)
    }
  },

  [ERROR] (state) {
    state.error = true
  },

  [INCREMENT] (state) {
    ++state.currentStep
  },

  [DECREMENT] (state) {
    --state.currentStep
  },

  [SET_STEP] (state, payload) {
    state.currentStep = payload
  },

  [VALIDATE_STEP] (state, payload) {
    const step = state.steps.find(step => step.id === payload.id)
    const index = state.steps.indexOf(step)
    if (index > -1) {
      state.steps[index].state = payload.state
    }
  },

  [VALID] (state) {
    const step = state.steps.find(step => step.id === state.currentStep)
    const index = state.steps.indexOf(step)
    if (index > -1) {
      state.steps[index].state = true
    }
  },

  [VALIDATE_ALL] (state) {
    state.steps.forEach(step => {
      step.state = true
    })
  },

  [INVALIDATE_ALL] (state) {
    state.steps.forEach(step => {
      step.state = false
    })
  },

  [INVALID] (state) {
    const step = state.steps.find(step => step.id === state.currentStep)
    const index = state.steps.indexOf(step)
    if (index > -1) {
      state.steps[index].state = false
    }
  },

  [CLOSE_ERROR] (state) {
    state.error = false
  },

  [SET_NEW] (state, payload) {
    state.isNew = !!payload
  },

  [SET_SENDING] (state, payload) {
    // console.log('SET_SENDING: ', payload)
    if (state.isSending !== payload) {
      state.isSending = payload
    }
  },

  [REMOVE_SENDING] (state, payload) {
    // console.log('REMOVE_SENDING: ', payload)
    if (state.isSending === payload) {
      state.isSending = ''
    }
  },

  [SET_VALIDATION_PROGRESS] (state, payload) {
    if (payload && payload.patternGuid) {
      state.validationProgress = Object.assign({}, state.validationProgress, {
        [payload.patternGuid]: Object.assign({}, payload, { receivedAt: Date.now() })
      })
    }
  },

  [CLEAR_VALIDATION_PROGRESS] (state, payload) {
    const guid = typeof payload === 'string' ? payload : payload && payload.patternGuid
    if (guid && state.validationProgress[guid]) {
      const progress = Object.assign({}, state.validationProgress)
      delete progress[guid]
      state.validationProgress = progress
    }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
